/**
 * The answer to "what is happening", in five seconds.
 *
 * Ordered by what is asked first, and the order changed when the strategy did.
 * State and capital lead, because whether the machine is running and how much
 * money is at work is the first question. What is actually running comes next:
 * the slots are where the money is, and they used to sit below a reference
 * table nobody opens the page for. What the strategy is watching follows, and
 * the evidence behind the return comes last, because it is evidence and not
 * news.
 *
 * Every figure was measured on the server and is rendered as it arrived.
 * Nothing here adds, subtracts or converts: the exactness of a money figure
 * survives exactly as far as the last place that leaves it alone.
 */
import { useState } from "react";
import { useSearchParams } from "react-router";

import { Disconnected, ErrorState, Forbidden, Loading, NotObserved } from "@/design-system/states/States";
import { PageHeader } from "@/design-system/PageHeader";
import type { OverviewResponse } from "@/shared/api";
import { useAccountAnalytics } from "@/shared/analytics/useAccountAnalytics";
import { CycleBook } from "@/shared/cycles/CycleBook";
import { LiveCycleSummary } from "@/shared/cycles/LiveCycleSummary";
import { PeriodControl } from "@/shared/period/PeriodControl";
import {
  periodLabel,
  readPeriod,
  writePeriod,
  type SelectedPeriod,
} from "@/shared/period/period";
import { PoolIndexPanel } from "@/shared/pools/PoolIndexPanel";
import { PoolPerformance } from "@/shared/pools/PoolPerformance";

import { AccountSummary } from "./AccountSummary";
import { ActionRequired } from "./ActionRequired";
import { MigrationPanel } from "./MigrationPanel";

import { PeriodResult } from "./PeriodResult";
import { BotActivity } from "./BotActivity";
import { Freshness } from "./Freshness";

import { SystemHealth } from "./SystemHealth";
import type { OverviewSources } from "./sources";
import { useOverview } from "@/shared/overview/useOverview";

const DESCRIPTION = "Что происходит на счёте: капитал, циклы и результат.";
const CLIENT_DESCRIPTION = "Ваш счёт: капитал, сделки и результат.";

type Props = {
  /**
   * The market-side regions, placed by whoever composes the page.
   *
   * Nodes rather than imports: this feature must not reach into another one,
   * or the two become impossible to change apart. The page decides where each
   * block sits; the route decides what goes in it.
   */
  readonly ranking?: React.ReactNode;
  readonly sources?: OverviewSources;
  /**
   * A client's own account: the same figures the owner reads, without the
   * platform's machinery — the bot's health, the strategy migration and the
   * operator's alerts (owner, 2026-10-31).
   */
  readonly client?: boolean;
};

/**
 * Empty funded slots from the backend occupancy read model.
 * This count is not Risk permission to open a cycle.
 */
function freeSlots(overview: OverviewResponse): number | undefined {
  return overview.idle_slots;
}

export function OverviewPage({ ranking, sources, client = false }: Props = {}): React.JSX.Element {
  const state = useOverview();
  const [search, setSearch] = useSearchParams();
  const [pool, setPool] = useState<string | null>(null);
  const period = readPeriod(search);
  // Two reads, and they are not interchangeable. The strip's figures are the
  // state of the account now and never move with the control; everything under
  // the control is an amount accumulated over the selected span.
  const selected = useAccountAnalytics(period);
  const todayPerformance = useAccountAnalytics({ since: null, until: null, preset: "today" });

  if (state.status === "loading") return <Loading title="Считываю счёт…" />;
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "not-observed") {
    return (
      <div className="flex flex-col gap-3">
        <PageHeader title="Обзор" description={DESCRIPTION} />
        <NotObserved />
      </div>
    );
  }
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать счёт"
        // Never the message the server sent: it can name a host, a database or
        // a credential, and none of that helps the owner.
        detail="Данные не пришли. Позиции и ордера на бирже от этого экрана не зависят."
      />
    );
  }

  const { overview } = state;

  return (
    <div className="flex flex-col gap-3">
      {/* Every screen opens with its own name (contract §4.1): a screenshot
          below the fold must still say which screen it is. */}
      <PageHeader title="Обзор" description={client ? CLIENT_DESCRIPTION : DESCRIPTION} />

      <Freshness
        asOf={overview.as_of}
        stale={overview.stale}
        ageSeconds={overview.age_seconds}
        budgetSeconds={overview.freshness_budget_seconds}
        connection={overview.connection}
      />

      {/* Shown above the figures, not instead of them: the account has not
          changed, only the view of it has. */}
      {overview.connection === "DISCONNECTED" && <Disconnected />}

      {/* The control governs the region below the account strip and nothing
          above it. Its range is stated beside the figures it governs, so a
          screenshot of one block stays unambiguous. */}
      <PeriodControl
        period={period}
        effectiveSince={selected.data?.effective_since ?? null}
        effectiveUntil={selected.data?.effective_until ?? null}
        onPeriod={(next: SelectedPeriod) => {
          setSearch(writePeriod(next), { replace: false });
        }}
      />

      {/* Ten regions on a twelve-column grid. The order is fixed by the Master
          Design Contract §12.1 and the reasoning is there: "what is running"
          is answered twice — a line here and the book at the bottom — so the
          nineteen-row table never pushes the rest of the page below the fold.

          Twelve columns rather than a stack of full-width bands. A band per
          region is how this page grew to 2400px with four figures per screen
          width, and related modules belong beside each other: a result next to
          the statistics that explain it, a chart next to the pools it charts. */}
      <div className="grid min-w-0 grid-cols-1 items-stretch gap-3 xl:grid-cols-12">
        <div className="min-w-0 xl:col-span-12">
          <LiveCycleSummary
            activeCycles={overview.active_cycles}
            slots={overview.cycles}
            freeSlots={freeSlots(overview)}
            onJump={() => {
              document
                .querySelector('[data-overview-region="active-cycles"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        </div>

        <div className="min-w-0 xl:col-span-12">
          <AccountSummary
            overview={overview}
            capital={overview.performance.capital ?? null}
            today={todayPerformance.data ?? null}
            loading={todayPerformance.isPending}
          />
        </div>

        {/* Directly under the account strip, and above the period control's
            region in reading order even though it sits below the control in
            the DOM: this block carries its own stated window and says so on
            its own line. It is here rather than lower because "which strategy
            holds the money" is the second question about this account, right
            after "how much is there" — and it was, until now, the one question
            no screen answered. Fixed order per §12.1. */}
        {!client && (
          <div className="min-w-0 xl:col-span-12">
            <MigrationPanel migration={overview.migration} />
          </div>
        )}

        <PeriodResult
          analytics={selected.data ?? null}
          label={periodLabel(period)}
          loading={selected.isPending}
        />

        {/* Chart and modules side by side, eight columns and four. Selecting a
            pool highlights its line; the comparison is the default state and a
            second click returns to it. */}
        {selected.data !== undefined && selected.data.pools.length > 0 && (
          <>
            <PoolIndexPanel
              realised={selected.data.realised}
              pools={selected.data.pools}
              selected={pool}
              onSelect={setPool}
            />
            <PoolPerformance
              pools={selected.data.pools}
              selected={pool}
              onSelect={setPool}
            />
          </>
        )}

        <div
          data-overview-region="market"
          className="min-w-0 xl:col-span-12"
        >
          {ranking}
        </div>

        {/* Seven columns and five, stretched to one height. Two panels of
            different heights side by side read as one unfinished. */}
        <div
          data-overview-region="health"
          className="grid min-w-0 grid-cols-1 items-stretch gap-3 xl:col-span-12 xl:grid-cols-12"
        >
          <div className={client ? "min-w-0 xl:col-span-12" : "min-w-0 xl:col-span-7"}>
            <BotActivity slots={overview.cycles} />
          </div>
          {!client && (
            <div className="min-w-0 xl:col-span-5">
              <SystemHealth overview={overview} sources={sources} />
            </div>
          )}
        </div>

        {/* Only when there is something to say. An empty alert panel on every
            load teaches the reader to skip the place alerts appear. */}
        {!client && (
          <div className="min-w-0 xl:col-span-12">
            <ActionRequired overview={overview} sources={sources} />
          </div>
        )}

        {/* Last, and nothing follows it. */}
        <div className="min-w-0 xl:col-span-12">
          <CycleBook
            slots={overview.cycles}
            title={`Активные циклы · ${String(overview.active_cycles)}`}
            region="active-cycles"
            // One region among ten: it scrolls inside itself rather than
            // deciding how long the page is.
            capped
            capitalLimit={overview.performance.capital?.target_slot_capital ?? null}
            asOf={overview.as_of}
            ageSeconds={overview.age_seconds}
            stale={overview.stale}
          />
        </div>
      </div>
    </div>
  );
}
